import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { AttachMoney, Info } from "@mui/icons-material";
import { CommonDialog } from "@/components/common/CommonDialog";
import type { BillingSendMode } from "../types/consultationsBilling.types";
import { formatStatsCurrency } from "../utils/getChargeAmount";

interface ConfirmBillingSendDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSendAll: () => Promise<void>;
  onConfirmSendMissing?: () => Promise<void>;
  onConfirmResendAll?: () => Promise<void>;
  loading?: boolean;
  error?: string;
  mode: BillingSendMode;
  groupInfo: {
    displayName: string;
    total: number;
    unsentTotal?: number;
    paymentsCount: number;
    unsentCount?: number;
    sentCount?: number;
  };
}

export function ConfirmBillingSendDialog({
  isOpen,
  onClose,
  onConfirmSendAll,
  onConfirmSendMissing,
  onConfirmResendAll,
  loading = false,
  error,
  mode,
  groupInfo,
}: ConfirmBillingSendDialogProps) {
  const unsentCount = groupInfo.unsentCount ?? groupInfo.paymentsCount;
  const sentCount = groupInfo.sentCount ?? 0;
  const unsentTotal = groupInfo.unsentTotal ?? groupInfo.total;

  const title = mode === "resend-all" ? "Reenviar cobranças" : "Enviar cobranças";

  return (
    <CommonDialog isOpen={isOpen} handleClose={onClose}>
      <Stack direction="column" spacing={3} alignItems="center" p={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Info sx={{ fontSize: 40, color: "info.main" }} />
          <Typography variant="h6" fontWeight={500} sx={{ color: "neutral1000.main" }}>
            {title}
          </Typography>
        </Stack>

        {mode === "partial" ? (
          <Typography variant="subtitle2" textAlign="center">
            {sentCount} de {groupInfo.paymentsCount}{" "}
            {groupInfo.paymentsCount === 1 ? "cobrança já foi enviada" : "cobranças já foram enviadas"},
            totalizando {formatStatsCurrency(groupInfo.total)}.{" "}
            {unsentCount > 0 ? (
              <>
                {unsentCount}{" "}
                {unsentCount === 1 ? "faltante soma" : "faltantes somam"}{" "}
                {formatStatsCurrency(unsentTotal)}.
              </>
            ) : null}{" "}
            Escolha se deseja enviar apenas as faltantes ou reenviar todas.
          </Typography>
        ) : mode === "resend-all" ? (
          <Typography variant="subtitle2" textAlign="center">
            Todas as {groupInfo.paymentsCount}{" "}
            {groupInfo.paymentsCount === 1
              ? "cobrança deste grupo já foi enviada"
              : "cobranças deste grupo já foram enviadas"}
            , totalizando {formatStatsCurrency(groupInfo.total)}. Deseja reenviar
            todas?
          </Typography>
        ) : (
          <Typography variant="subtitle2" textAlign="center">
            Você está prestes a enviar {groupInfo.paymentsCount}{" "}
            {groupInfo.paymentsCount === 1 ? "cobrança" : "cobranças"} totalizando{" "}
            {formatStatsCurrency(groupInfo.total)}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            backgroundColor: "neutral100.main",
            border: "1px solid",
            borderColor: "neutral300.main",
            borderRadius: 1,
            p: 2,
          }}
        >
          <Info sx={{ color: "neutral700.main" }} />
          <Typography variant="body2" color="neutral700.main">
            {mode === "partial"
              ? "Enviar faltantes manda cobrança só para quem ainda não recebeu. Reenviar todas dispara novamente para todo o grupo."
              : "Esta ação enviará cobranças via WhatsApp. Certifique-se de que todos os dados estão corretos antes de prosseguir."}
          </Typography>
        </Stack>

        {error && (
          <Typography color="error.main" variant="body2">
            {error}
          </Typography>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>

          {mode === "partial" ? (
            <>
              <Tooltip title={`Enviar ${unsentCount} cobrança(s) ainda não enviada(s)`}>
                <span>
                  <Button
                    variant="contained"
                    sx={{ color: "neutral0.main" }}
                    startIcon={<AttachMoney />}
                    onClick={onConfirmSendMissing}
                    disabled={loading || unsentCount === 0}
                  >
                    Enviar faltantes ({unsentCount})
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Reenviar cobrança para todo o grupo">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<AttachMoney />}
                    onClick={onConfirmResendAll}
                    disabled={loading}
                  >
                    Reenviar todas ({groupInfo.paymentsCount})
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : mode === "resend-all" ? (
            <Tooltip title="Reenviar cobrança para todo o grupo">
              <span>
                <Button
                  variant="contained"
                  sx={{ color: "neutral0.main" }}
                  startIcon={<AttachMoney />}
                  onClick={onConfirmResendAll}
                  disabled={loading}
                >
                  Reenviar todas
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Enviar cobranças para todos os pacientes do grupo">
              <span>
                <Button
                  variant="contained"
                  sx={{ color: "neutral0.main" }}
                  startIcon={<AttachMoney />}
                  onClick={onConfirmSendAll}
                  disabled={loading}
                >
                  Confirmar envio
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </CommonDialog>
  );
}
